import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { useApp } from '../context/AppContext';
import { NDP6_TAXONOMY } from '../data/ndp6Taxonomy';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, PieChart, Pie, Legend } from 'recharts';

export const NDP6Progress = () => {
  const { data } = useApp();
  const navigate = useNavigate();
  const [activePillar, setActivePillar] = useState('all');
  const [activeView, setActiveView] = useState('overview'); // overview, pillars, ministries, projects
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPillar, setSelectedPillar] = useState(null);

  // Compute cabinet decision counts per NDP6 pillar
  const cabinetByPillar = useMemo(() => {
    const decisions = data?.cabinetDecisions || [];
    const byPillar = {};
    decisions.forEach(d => {
      const pillarId = d?.ndp6PillarId;
      if (pillarId == null) return;
      if (!byPillar[pillarId]) {
        byPillar[pillarId] = { total: 0, completed: 0, inProgress: 0, overdue: 0, items: [] };
      }
      byPillar[pillarId].total += 1;
      if (d?.status === 'Completed') byPillar[pillarId].completed += 1;
      else if (d?.status === 'In Progress') byPillar[pillarId].inProgress += 1;
      else if (d?.status === 'Overdue') byPillar[pillarId].overdue += 1;
      byPillar[pillarId].items.push({
        id: d?.id,
        title: d?.title,
        status: d?.status,
        progress: d?.overallProgress ?? 0,
      });
    });
    return byPillar;
  }, [data?.cabinetDecisions]);

  // Extended NDP6 data with comprehensive tracking
  const ndp6Data = {
    fiscalYear: '2024/2025',
    reportingPeriod: 'Q2 FY 2024/25',
    lastUpdated: '2025-01-20T16:00:00Z',
    
    pillars: [
      {
        id: 1,
        name: 'Economic Advancement',
        code: 'EA',
        progress: 58,
        status: 'At Risk',
        targetProgress: 65,
        budget: 45000000000,
        disbursed: 26100000000,
        budgetExecution: 58,
        projectsTotal: 67,
        projectsOnTrack: 38,
        projectsAtRisk: 19,
        projectsDelayed: 10,
        kpis: [
          { metric: 'GDP Growth Rate', target: 4.5, actual: 3.8, unit: '%', status: 'Behind' },
          { metric: 'Infrastructure Investment', target: 15000000000, actual: 11200000000, unit: 'NAD', status: 'Behind' },
          { metric: 'Job Creation', target: 25000, actual: 18400, unit: 'jobs', status: 'Behind' },
          { metric: 'SME Support Grants', target: 500, actual: 423, unit: 'grants', status: 'At Risk' }
        ],
        thematicAreas: [
          {
            id: '1.1',
            name: 'Infrastructure Development',
            progress: 62,
            budget: 25000000000,
            disbursed: 15500000000,
            projects: 35,
            focusAreas: [
              { name: 'Roads & Bridges', progress: 68, projects: 15 },
              { name: 'Energy Security', progress: 55, projects: 12 },
              { name: 'Water Infrastructure', progress: 64, projects: 8 }
            ]
          },
          {
            id: '1.2',
            name: 'Industrialization',
            progress: 52,
            budget: 12000000000,
            disbursed: 6240000000,
            projects: 18,
            focusAreas: [
              { name: 'Manufacturing', progress: 48, projects: 8 },
              { name: 'SME Support', progress: 58, projects: 6 },
              { name: 'Mining Value Addition', progress: 50, projects: 4 }
            ]
          },
          {
            id: '1.3',
            name: 'Agriculture & Food Security',
            progress: 60,
            budget: 8000000000,
            disbursed: 4800000000,
            projects: 14,
            focusAreas: [
              { name: 'Irrigation Schemes', progress: 72, projects: 5 },
              { name: 'Livestock Development', progress: 55, projects: 6 },
              { name: 'Agricultural Mechanization', progress: 53, projects: 3 }
            ]
          }
        ],
        risks: [
          { title: 'Budget execution below target', severity: 'high', impact: 'Delays in infrastructure projects' },
          { title: 'Contractor capacity constraints', severity: 'medium', impact: 'Project timeline extensions' }
        ],
        topPerformers: ['Ministry of Works & Transport', 'Ministry of Agriculture'],
        underperformers: ['Ministry of Industrialization']
      },
      {
        id: 2,
        name: 'Social Transformation',
        code: 'ST',
        progress: 72,
        status: 'On Track',
        targetProgress: 70,
        budget: 38000000000,
        disbursed: 27360000000,
        budgetExecution: 72,
        projectsTotal: 89,
        projectsOnTrack: 64,
        projectsAtRisk: 18,
        projectsDelayed: 7,
        kpis: [
          { metric: 'Primary School Enrollment', target: 95, actual: 93, unit: '%', status: 'On Track' },
          { metric: 'Healthcare Access', target: 85, actual: 78, unit: '%', status: 'At Risk' },
          { metric: 'Poverty Reduction', target: 15, actual: 17.2, unit: '%', status: 'Behind' },
          { metric: 'Social Housing Units', target: 5000, actual: 4200, unit: 'units', status: 'On Track' }
        ],
        thematicAreas: [
          {
            id: '2.1',
            name: 'Education & Skills Development',
            progress: 78,
            budget: 18000000000,
            disbursed: 14040000000,
            projects: 42,
            focusAreas: [
              { name: 'Vocational Training', progress: 82, projects: 12 },
              { name: 'Primary Education', progress: 76, projects: 20 },
              { name: 'Higher Education', progress: 75, projects: 10 }
            ]
          },
          {
            id: '2.2',
            name: 'Health & Wellness',
            progress: 68,
            budget: 15000000000,
            disbursed: 10200000000,
            projects: 31,
            focusAreas: [
              { name: 'Universal Health Coverage', progress: 65, projects: 15 },
              { name: 'Hospital Infrastructure', progress: 72, projects: 10 },
              { name: 'Public Health Programs', progress: 68, projects: 6 }
            ]
          },
          {
            id: '2.3',
            name: 'Social Protection',
            progress: 70,
            budget: 5000000000,
            disbursed: 3500000000,
            projects: 16,
            focusAreas: [
              { name: 'Social Housing', progress: 84, projects: 8 },
              { name: 'Food Security Programs', progress: 62, projects: 5 },
              { name: 'Poverty Alleviation', progress: 64, projects: 3 }
            ]
          }
        ],
        risks: [
          { title: 'Healthcare infrastructure delays', severity: 'medium', impact: 'UHC rollout postponed' }
        ],
        topPerformers: ['Ministry of Education', 'Ministry of Urban Development'],
        underperformers: ['Ministry of Health & Social Services']
      },
      {
        id: 3,
        name: 'Environmental Sustainability',
        code: 'ES',
        progress: 88,
        status: 'On Track',
        targetProgress: 75,
        budget: 8500000000,
        disbursed: 7480000000,
        budgetExecution: 88,
        projectsTotal: 34,
        projectsOnTrack: 29,
        projectsAtRisk: 4,
        projectsDelayed: 1,
        kpis: [
          { metric: 'Renewable Energy Capacity', target: 400, actual: 352, unit: 'MW', status: 'On Track' },
          { metric: 'Protected Areas', target: 45, actual: 43.2, unit: '%', status: 'On Track' },
          { metric: 'Carbon Emissions Reduction', target: 10, actual: 8.5, unit: '%', status: 'On Track' },
          { metric: 'Waste Recycling Rate', target: 25, actual: 28, unit: '%', status: 'Ahead' }
        ],
        thematicAreas: [
          {
            id: '3.1',
            name: 'Climate Action',
            progress: 90,
            budget: 4500000000,
            disbursed: 4050000000,
            projects: 18,
            focusAreas: [
              { name: 'Renewable Energy', progress: 92, projects: 10 },
              { name: 'Conservation', progress: 88, projects: 5 },
              { name: 'Disaster Management', progress: 90, projects: 3 }
            ]
          },
          {
            id: '3.2',
            name: 'Natural Resource Management',
            progress: 86,
            budget: 4000000000,
            disbursed: 3440000000,
            projects: 16,
            focusAreas: [
              { name: 'Water Conservation', progress: 88, projects: 8 },
              { name: 'Wildlife Protection', progress: 85, projects: 5 },
              { name: 'Sustainable Forestry', progress: 84, projects: 3 }
            ]
          }
        ],
        risks: [
          { title: 'Drought impact on conservation', severity: 'low', impact: 'Minor project adjustments needed' }
        ],
        topPerformers: ['Ministry of Environment', 'Ministry of Mines & Energy'],
        underperformers: []
      },
      {
        id: 4,
        name: 'Good Governance',
        code: 'GG',
        progress: 64,
        status: 'At Risk',
        targetProgress: 70,
        budget: 12000000000,
        disbursed: 7680000000,
        budgetExecution: 64,
        projectsTotal: 45,
        projectsOnTrack: 28,
        projectsAtRisk: 12,
        projectsDelayed: 5,
        kpis: [
          { metric: 'e-Government Services', target: 50, actual: 38, unit: 'services', status: 'Behind' },
          { metric: 'Corruption Perception Index', target: 55, actual: 51, unit: 'score', status: 'At Risk' },
          { metric: 'Public Service Delivery Score', target: 75, actual: 68, unit: '%', status: 'At Risk' },
          { metric: 'Audit Compliance Rate', target: 90, actual: 72, unit: '%', status: 'Behind' }
        ],
        thematicAreas: [
          {
            id: '4.1',
            name: 'Public Service Reform',
            progress: 68,
            budget: 6000000000,
            disbursed: 4080000000,
            projects: 22,
            focusAreas: [
              { name: 'Digitalization', progress: 75, projects: 12 },
              { name: 'Accountability', progress: 62, projects: 6 },
              { name: 'Service Delivery', progress: 66, projects: 4 }
            ]
          },
          {
            id: '4.2',
            name: 'Anti-Corruption & Transparency',
            progress: 58,
            budget: 3500000000,
            disbursed: 2030000000,
            projects: 13,
            focusAreas: [
              { name: 'Whistleblower Protection', progress: 55, projects: 4 },
              { name: 'Asset Declaration', progress: 60, projects: 5 },
              { name: 'Procurement Transparency', progress: 59, projects: 4 }
            ]
          },
          {
            id: '4.3',
            name: 'Decentralization',
            progress: 66,
            budget: 2500000000,
            disbursed: 1650000000,
            projects: 10,
            focusAreas: [
              { name: 'Regional Council Capacity', progress: 70, projects: 5 },
              { name: 'Local Government Finance', progress: 62, projects: 3 },
              { name: 'Community Participation', progress: 66, projects: 2 }
            ]
          }
        ],
        risks: [
          { title: 'Digital transformation delays', severity: 'high', impact: 'e-Government targets missed' },
          { title: 'Audit capacity constraints', severity: 'medium', impact: 'Compliance monitoring gaps' }
        ],
        topPerformers: ['Ministry of ICT', 'Office of the Prime Minister'],
        underperformers: ['Anti-Corruption Commission', 'Public Service Commission']
      }
    ],

    ministryPerformance: [
      { 
        ministry: 'Ministry of Environment, Forestry & Tourism', 
        pillar: 'Environmental Sustainability', 
        score: 92, 
        projects: 18, 
        budgetExecution: 90,
        status: 'On Track'
      },
      { 
        ministry: 'Ministry of Education, Arts & Culture', 
        pillar: 'Social Transformation', 
        score: 78, 
        projects: 42, 
        budgetExecution: 78,
        status: 'On Track'
      },
      { 
        ministry: 'Ministry of ICT', 
        pillar: 'Good Governance', 
        score: 75, 
        projects: 12, 
        budgetExecution: 82,
        status: 'On Track'
      },
      { 
        ministry: 'Ministry of Mines & Energy', 
        pillar: 'Environmental Sustainability', 
        score: 85, 
        projects: 16, 
        budgetExecution: 86,
        status: 'On Track'
      },
      { 
        ministry: 'Ministry of Works & Transport', 
        pillar: 'Economic Advancement', 
        score: 62, 
        projects: 35, 
        budgetExecution: 58,
        status: 'At Risk'
      },
      { 
        ministry: 'Ministry of Health & Social Services', 
        pillar: 'Social Transformation', 
        score: 68, 
        projects: 31, 
        budgetExecution: 68,
        status: 'At Risk'
      },
      { 
        ministry: 'Ministry of Agriculture, Water & Land Reform', 
        pillar: 'Economic Advancement', 
        score: 60, 
        projects: 14, 
        budgetExecution: 60,
        status: 'At Risk'
      },
      { 
        ministry: 'Anti-Corruption Commission', 
        pillar: 'Good Governance', 
        score: 54, 
        projects: 8, 
        budgetExecution: 52,
        status: 'Delayed'
      }
    ],

    quarterlyTrends: [
      { quarter: 'Q1 2023/24', EA: 52, ST: 65, ES: 82, GG: 58 },
      { quarter: 'Q2 2023/24', EA: 54, ST: 67, ES: 84, GG: 60 },
      { quarter: 'Q3 2023/24', EA: 55, ST: 69, ES: 85, GG: 61 },
      { quarter: 'Q4 2023/24', EA: 56, ST: 70, ES: 86, GG: 62 },
      { quarter: 'Q1 2024/25', EA: 57, ST: 71, ES: 87, GG: 63 },
      { quarter: 'Q2 2024/25', EA: 58, ST: 72, ES: 88, GG: 64 }
    ],

    criticalGaps: [
      {
        pillar: 'Economic Advancement',
        gap: 'Infrastructure project delays due to contractor capacity',
        impact: 'High',
        recommendation: 'Fast-track contractor development programs',
        responsibleOMA: 'Ministry of Works & Transport'
      },
      {
        pillar: 'Good Governance',
        gap: 'Low audit compliance rate (72% vs 90% target)',
        impact: 'High',
        recommendation: 'Mandatory quarterly compliance reviews',
        responsibleOMA: 'All Ministries'
      },
      {
        pillar: 'Social Transformation',
        gap: 'Healthcare access below target (78% vs 85%)',
        impact: 'Medium',
        recommendation: 'Accelerate rural clinic construction',
        responsibleOMA: 'Ministry of Health & Social Services'
      },
      {
        pillar: 'Economic Advancement',
        gap: 'Budget execution at 58% (target 65%)',
        impact: 'Medium',
        recommendation: 'Implement budget acceleration plans',
        responsibleOMA: 'Ministry of Finance'
      }
    ]
  };

  const { filteredData, stats } = useMemo(() => {
    let filtered = ndp6Data.pillars;

    if (activePillar !== 'all') {
      filtered = filtered.filter(p => p.code === activePillar);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.code.toLowerCase().includes(query) ||
        p.thematicAreas.some(ta => ta.name.toLowerCase().includes(query))
      );
    }

    const stats = {
      totalBudget: ndp6Data.pillars.reduce((sum, p) => sum + p.budget, 0),
      totalDisbursed: ndp6Data.pillars.reduce((sum, p) => sum + p.disbursed, 0),
      avgProgress: Math.round(ndp6Data.pillars.reduce((sum, p) => sum + p.progress, 0) / ndp6Data.pillars.length),
      avgBudgetExecution: Math.round(ndp6Data.pillars.reduce((sum, p) => sum + p.budgetExecution, 0) / ndp6Data.pillars.length),
      totalProjects: ndp6Data.pillars.reduce((sum, p) => sum + p.projectsTotal, 0),
      projectsOnTrack: ndp6Data.pillars.reduce((sum, p) => sum + p.projectsOnTrack, 0),
      projectsAtRisk: ndp6Data.pillars.reduce((sum, p) => sum + p.projectsAtRisk, 0),
      projectsDelayed: ndp6Data.pillars.reduce((sum, p) => sum + p.projectsDelayed, 0)
    };

    return { filteredData: filtered, stats };
  }, [activePillar, searchQuery, ndp6Data.pillars]);

  const pillarTabs = [
    { id: 'all', label: 'All Pillars', icon: FiIcons.FiGrid },
    { id: 'EA', label: 'Economic', icon: FiIcons.FiTrendingUp },
    { id: 'ST', label: 'Social', icon: FiIcons.FiUsers },
    { id: 'ES', label: 'Environmental', icon: FiIcons.FiGlobe },
    { id: 'GG', label: 'Governance', icon: FiIcons.FiShield }
  ];

  const viewTabs = [
    { id: 'overview', label: 'Overview', icon: FiIcons.FiGrid },
    { id: 'pillars', label: 'Pillar Deep Dive', icon: FiIcons.FiLayers },
    { id: 'ministries', label: 'Ministry Performance', icon: FiIcons.FiBuilding },
    { id: 'trends', label: 'Trends & Gaps', icon: FiIcons.FiTrendingUp }
  ];

  const formatCurrency = (value) => {
    if (value >= 1e9) return `N$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `N$${(value / 1e6).toFixed(1)}M`;
    return `N$${value.toLocaleString()}`;
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">NDP6 Progress Monitoring</h1>
          <p className="text-slate-500">
            National Development Plan 6 (2022-2027) - Integrated Monitoring & Evaluation Platform
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="px-2 py-1 bg-grn-navy text-white rounded font-bold">
              {ndp6Data.reportingPeriod}
            </span>
            <span className="text-slate-500">
              Last Updated: {format(new Date(ndp6Data.lastUpdated), 'dd MMM yyyy, HH:mm')}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiDownload} />
            Export Report
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiUpload} />
            Submit Update
          </button>
        </div>
      </div>

      {/* National Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center p-4 border-l-4 border-l-grn-navy">
          <div className="text-3xl font-black text-grn-navy">{stats.avgProgress}%</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Avg NDP6 Progress
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-green-500">
          <div className="text-3xl font-black text-green-700">{stats.avgBudgetExecution}%</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Budget Execution
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-blue-500">
          <div className="text-3xl font-black text-blue-700">{stats.totalProjects}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Total Projects
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-amber-500">
          <div className="text-3xl font-black text-amber-700">{formatCurrency(stats.totalDisbursed)}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Funds Disbursed
          </div>
        </Card>
      </div>

      {/* View Tabs */}
      <Card>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {viewTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap',
                activeView === tab.id
                  ? 'bg-grn-navy text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
              )}
            >
              <SafeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Overview View */}
      {activeView === 'overview' && (
        <>
          {/* Pillar Progress Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ndp6Data.pillars.map(pillar => (
              <Card
                key={pillar.id}
                className={clsx(
                  'hover:shadow-lg transition-all border-l-4',
                  pillar.status === 'On Track' ? 'border-l-green-500' :
                  pillar.status === 'At Risk' ? 'border-l-amber-500' :
                  'border-l-red-500'
                )}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2 py-1 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded">
                          PILLAR {pillar.id}
                        </span>
                        <Badge status={pillar.status} size="sm" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">{pillar.name}</h3>
                      <p className="text-xs text-slate-500">
                        {pillar.projectsTotal} projects • {formatCurrency(pillar.budget)} allocated
                      </p>
                    </div>
                    <div className="text-center ml-4">
                      <div className={clsx(
                        'text-4xl font-black mb-1',
                        pillar.progress >= pillar.targetProgress ? 'text-green-600' :
                        pillar.progress >= pillar.targetProgress - 10 ? 'text-amber-600' :
                        'text-red-600'
                      )}>
                        {pillar.progress}%
                      </div>
                      <div className="text-xs text-slate-500">
                        Target: {pillar.targetProgress}%
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between items-center mb-1 text-xs">
                      <span className="text-slate-600 font-medium">Overall Progress</span>
                      <span className="text-slate-500">{pillar.progress}% of {pillar.targetProgress}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={clsx(
                          'h-3 rounded-full transition-all',
                          pillar.progress >= pillar.targetProgress ? 'bg-green-500' :
                          pillar.progress >= pillar.targetProgress - 10 ? 'bg-amber-500' :
                          'bg-red-500'
                        )}
                        style={{ width: `${(pillar.progress / pillar.targetProgress) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-2xl font-black text-green-700">{pillar.projectsOnTrack}</div>
                      <div className="text-xs text-green-600 uppercase tracking-wider">On Track</div>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="text-2xl font-black text-amber-700">{pillar.projectsAtRisk}</div>
                      <div className="text-xs text-amber-600 uppercase tracking-wider">At Risk</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="text-2xl font-black text-red-700">{pillar.projectsDelayed}</div>
                      <div className="text-xs text-red-600 uppercase tracking-wider">Delayed</div>
                    </div>
                  </div>

                  {/* Budget Execution */}
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Budget Execution
                      </span>
                      <span className="text-sm font-bold text-slate-800">
                        {pillar.budgetExecution}%
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-600">
                      <div>
                        <span className="text-slate-500">Allocated:</span>
                        <span className="font-bold ml-1">{formatCurrency(pillar.budget)}</span>
                      </div>
                      <span>•</span>
                      <div>
                        <span className="text-slate-500">Disbursed:</span>
                        <span className="font-bold ml-1">{formatCurrency(pillar.disbursed)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Cabinet Decisions */}
                  {(cabinetByPillar[pillar.id]?.total ?? 0) > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <SafeIcon icon={FiIcons.FiBookOpen} className="text-grn-gold" />
                          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Cabinet Decisions
                          </span>
                        </div>
                        <span className="px-2 py-0.5 bg-grn-gold/10 text-grn-gold text-xs font-bold rounded">
                          {cabinetByPillar[pillar.id]?.total} Decision{cabinetByPillar[pillar.id]?.total !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-600 mb-2">
                        {(cabinetByPillar[pillar.id]?.completed ?? 0) > 0 && (
                          <span className="text-green-600 font-medium">
                            {cabinetByPillar[pillar.id]?.completed} Completed
                          </span>
                        )}
                        {(cabinetByPillar[pillar.id]?.inProgress ?? 0) > 0 && (
                          <span className="text-blue-600 font-medium">
                            {cabinetByPillar[pillar.id]?.inProgress} In Progress
                          </span>
                        )}
                        {(cabinetByPillar[pillar.id]?.overdue ?? 0) > 0 && (
                          <span className="text-red-600 font-medium">
                            {cabinetByPillar[pillar.id]?.overdue} Overdue
                          </span>
                        )}
                      </div>
                      {cabinetByPillar[pillar.id]?.total > 0 && (
                        <div className="w-full bg-slate-200 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full bg-grn-gold"
                            style={{
                              width: `${Math.round(((cabinetByPillar[pillar.id]?.completed ?? 0) / cabinetByPillar[pillar.id]?.total) * 100)}%`
                            }}
                          ></div>
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/cabinet-decisions');
                        }}
                        className="mt-2 text-xs text-grn-gold hover:text-grn-gold/80 font-medium flex items-center gap-1 transition-colors"
                      >
                        View Cabinet Decisions
                        <SafeIcon icon={FiIcons.FiArrowRight} className="text-xs" />
                      </button>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={() => {
                      setSelectedPillar(pillar);
                      setActiveView('pillars');
                    }}
                    className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiIcons.FiEye} />
                    View Detailed Breakdown
                  </button>
                </div>
              </Card>
            ))}
          </div>

          {/* Quarterly Trends Chart */}
          <Card title="Quarterly Progress Trends">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ndp6Data.quarterlyTrends}>
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="EA" stroke="#003580" strokeWidth={2} name="Economic Advancement" />
                  <Line type="monotone" dataKey="ST" stroke="#009543" strokeWidth={2} name="Social Transformation" />
                  <Line type="monotone" dataKey="ES" stroke="#0085CA" strokeWidth={2} name="Environmental Sustainability" />
                  <Line type="monotone" dataKey="GG" stroke="#C4953B" strokeWidth={2} name="Good Governance" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}

      {/* Pillar Deep Dive View */}
      {activeView === 'pillars' && (
        <>
          {/* Pillar Selector */}
          <Card>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {ndp6Data.pillars.map(pillar => (
                <button
                  key={pillar.id}
                  onClick={() => setSelectedPillar(pillar)}
                  className={clsx(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                    selectedPillar?.id === pillar.id
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  Pillar {pillar.id}: {pillar.name}
                </button>
              ))}
            </div>
          </Card>

          {selectedPillar && (
            <>
              {/* Pillar Header */}
              <Card className="bg-gradient-to-r from-grn-navy to-blue-900 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded">
                        PILLAR {selectedPillar.id}
                      </span>
                      <span className={clsx(
                        'px-3 py-1 rounded text-xs font-bold',
                        selectedPillar.status === 'On Track' ? 'bg-green-500 text-white' :
                        selectedPillar.status === 'At Risk' ? 'bg-amber-500 text-white' :
                        'bg-red-500 text-white'
                      )}>
                        {selectedPillar.status}
                      </span>
                      {(cabinetByPillar[selectedPillar.id]?.total ?? 0) > 0 && (
                        <span className="px-3 py-1 bg-grn-gold/30 text-white text-xs font-bold rounded flex items-center gap-1">
                          <SafeIcon icon={FiIcons.FiBookOpen} className="text-xs" />
                          {cabinetByPillar[selectedPillar.id]?.total} Cabinet Decision{cabinetByPillar[selectedPillar.id]?.total !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold mb-2">{selectedPillar.name}</h2>
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <div>
                        <span className="opacity-80">Progress:</span>
                        <span className="font-bold ml-2">{selectedPillar.progress}% / {selectedPillar.targetProgress}%</span>
                      </div>
                      <span>•</span>
                      <div>
                        <span className="opacity-80">Budget Execution:</span>
                        <span className="font-bold ml-2">{selectedPillar.budgetExecution}%</span>
                      </div>
                      <span>•</span>
                      <div>
                        <span className="opacity-80">Projects:</span>
                        <span className="font-bold ml-2">{selectedPillar.projectsTotal}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-5xl font-black mb-1">{selectedPillar.progress}%</div>
                    <div className="text-sm opacity-80">Overall Progress</div>
                  </div>
                </div>
              </Card>

              {/* KPIs */}
              <Card title="Key Performance Indicators">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedPillar.kpis.map((kpi, idx) => {
                    const percentage = kpi.target > 0 ? (kpi.actual / kpi.target) * 100 : 0;
                    const isGood = kpi.status === 'On Track' || kpi.status === 'Ahead';
                    
                    return (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="text-sm font-bold text-slate-800">{kpi.metric}</h4>
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-bold',
                            isGood ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          )}>
                            {kpi.status}
                          </span>
                        </div>
                        <div className="flex items-end justify-between mb-2">
                          <div>
                            <div className="text-2xl font-black text-slate-900">
                              {typeof kpi.actual === 'number' && kpi.unit === 'NAD' 
                                ? formatCurrency(kpi.actual)
                                : kpi.actual.toLocaleString()
                              }
                            </div>
                            <div className="text-xs text-slate-500">
                              Target: {typeof kpi.target === 'number' && kpi.unit === 'NAD'
                                ? formatCurrency(kpi.target)
                                : kpi.target.toLocaleString()
                              } {kpi.unit !== 'NAD' ? kpi.unit : ''}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={clsx(
                              'text-lg font-bold',
                              isGood ? 'text-green-600' : 'text-red-600'
                            )}>
                              {percentage.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className={clsx(
                              'h-2 rounded-full',
                              isGood ? 'bg-green-500' : 'bg-red-500'
                            )}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Thematic Areas */}
              <Card title="Thematic Areas Breakdown">
                <div className="space-y-6">
                  {selectedPillar.thematicAreas.map((area, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-slate-900 mb-1">{area.name}</h4>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>{area.projects} projects</span>
                            <span>•</span>
                            <span>Budget: {formatCurrency(area.budget)}</span>
                            <span>•</span>
                            <span>Disbursed: {formatCurrency(area.disbursed)}</span>
                          </div>
                        </div>
                        <div className="text-center ml-4">
                          <div className={clsx(
                            'text-3xl font-black',
                            area.progress >= 70 ? 'text-green-600' :
                            area.progress >= 50 ? 'text-amber-600' :
                            'text-red-600'
                          )}>
                            {area.progress}%
                          </div>
                        </div>
                      </div>

                      {/* Focus Areas */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {area.focusAreas.map((focus, fIdx) => (
                          <div key={fIdx} className="p-3 bg-slate-50 rounded border border-slate-200">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs font-medium text-slate-700">{focus.name}</span>
                              <span className="text-xs font-bold text-slate-800">{focus.progress}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-1.5">
                              <div
                                className={clsx(
                                  'h-1.5 rounded-full',
                                  focus.progress >= 70 ? 'bg-green-500' :
                                  focus.progress >= 50 ? 'bg-amber-500' :
                                  'bg-red-500'
                                )}
                                style={{ width: `${focus.progress}%` }}
                              ></div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {focus.projects} project{focus.projects !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Risks & Top/Underperformers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card title="Identified Risks">
                  <div className="space-y-3">
                    {selectedPillar.risks.map((risk, idx) => (
                      <div
                        key={idx}
                        className={clsx(
                          'p-3 rounded-lg border',
                          risk.severity === 'high' ? 'bg-red-50 border-red-200' :
                          risk.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                          'bg-blue-50 border-blue-200'
                        )}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <SafeIcon
                            icon={FiIcons.FiAlertTriangle}
                            className={clsx(
                              'mt-0.5',
                              risk.severity === 'high' ? 'text-red-600' :
                              risk.severity === 'medium' ? 'text-amber-600' :
                              'text-blue-600'
                            )}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h5 className="text-sm font-bold text-slate-800">{risk.title}</h5>
                              <span className={clsx(
                                'px-2 py-0.5 rounded text-xs font-bold uppercase',
                                risk.severity === 'high' ? 'bg-red-100 text-red-700' :
                                risk.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              )}>
                                {risk.severity}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600">
                              <strong>Impact:</strong> {risk.impact}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card title="OMA Performance">
                  <div className="space-y-3">
                    <div>
                      <h5 className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">
                        Top Performers
                      </h5>
                      {selectedPillar.topPerformers.map((oma, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 rounded mb-2">
                          <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600" />
                          <span className="text-sm text-slate-800">{oma}</span>
                        </div>
                      ))}
                    </div>
                    {selectedPillar.underperformers.length > 0 && (
                      <div>
                        <h5 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">
                          Needs Improvement
                        </h5>
                        {selectedPillar.underperformers.map((oma, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-red-50 rounded mb-2">
                            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600" />
                            <span className="text-sm text-slate-800">{oma}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Cabinet Decisions Aligned to This Pillar */}
              {(cabinetByPillar[selectedPillar.id]?.total ?? 0) > 0 && (
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiIcons.FiBookOpen} className="text-lg text-grn-gold" />
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                        Cabinet Decisions Aligned to This Pillar
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-grn-gold/10 text-grn-gold text-sm font-bold rounded">
                        {cabinetByPillar[selectedPillar.id]?.total} Decision{cabinetByPillar[selectedPillar.id]?.total !== 1 ? 's' : ''}
                      </span>
                      {(cabinetByPillar[selectedPillar.id]?.total ?? 0) > 0 && (
                        <span className="text-xs text-slate-500">
                          {Math.round(((cabinetByPillar[selectedPillar.id]?.completed ?? 0) / cabinetByPillar[selectedPillar.id]?.total) * 100)}% Completion Rate
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                      <div className="text-2xl font-black text-slate-800">{cabinetByPillar[selectedPillar.id]?.total ?? 0}</div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">Total</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                      <div className="text-2xl font-black text-green-700">{cabinetByPillar[selectedPillar.id]?.completed ?? 0}</div>
                      <div className="text-xs text-green-600 uppercase tracking-wider">Completed</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <div className="text-2xl font-black text-blue-700">{cabinetByPillar[selectedPillar.id]?.inProgress ?? 0}</div>
                      <div className="text-xs text-blue-600 uppercase tracking-wider">In Progress</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                      <div className="text-2xl font-black text-red-700">{cabinetByPillar[selectedPillar.id]?.overdue ?? 0}</div>
                      <div className="text-xs text-red-600 uppercase tracking-wider">Overdue</div>
                    </div>
                  </div>

                  {/* Decision Items */}
                  <div className="space-y-2">
                    {cabinetByPillar[selectedPillar.id]?.items?.map((item) => (
                      <div
                        key={item?.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/cabinet-decisions/${item?.id}`)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={clsx(
                            'w-2 h-2 rounded-full shrink-0',
                            item?.status === 'Completed' ? 'bg-green-500' :
                            item?.status === 'In Progress' ? 'bg-blue-500' :
                            item?.status === 'Overdue' ? 'bg-red-500' :
                            'bg-slate-400'
                          )} />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{item?.title}</div>
                            <div className="text-xs text-slate-500">{item?.id}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          <Badge status={item?.status} size="sm" />
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <span className="font-bold">{item?.progress ?? 0}%</span>
                          </div>
                          <SafeIcon icon={FiIcons.FiChevronRight} className="text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Link to Cabinet Decisions Page */}
                  <button
                    onClick={() => navigate('/cabinet-decisions')}
                    className="mt-4 w-full px-4 py-2 bg-white border border-grn-gold/30 text-grn-gold rounded-lg text-sm font-medium hover:bg-grn-gold/5 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiIcons.FiBookOpen} />
                    View All Cabinet Decisions
                    <SafeIcon icon={FiIcons.FiArrowRight} />
                  </button>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* Ministry Performance View */}
      {activeView === 'ministries' && (
        <>
          <Card title="Ministry Performance Ranking">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Rank</th>
                    <th className="px-4 py-3 text-left font-medium">Ministry</th>
                    <th className="px-4 py-3 text-left font-medium">NDP6 Pillar</th>
                    <th className="px-4 py-3 text-left font-medium">Performance Score</th>
                    <th className="px-4 py-3 text-left font-medium">Budget Execution</th>
                    <th className="px-4 py-3 text-left font-medium">Projects</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ndp6Data.ministryPerformance
                    .sort((a, b) => b.score - a.score)
                    .map((ministry, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className={clsx(
                            'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm',
                            idx === 0 ? 'bg-grn-gold text-white' :
                            idx === 1 ? 'bg-slate-300 text-slate-700' :
                            idx === 2 ? 'bg-amber-200 text-amber-800' :
                            'bg-slate-100 text-slate-600'
                          )}>
                            {idx + 1}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">
                          {ministry.ministry}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {ministry.pillar}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-slate-200 rounded-full h-2">
                              <div
                                className={clsx(
                                  'h-2 rounded-full',
                                  ministry.score >= 80 ? 'bg-green-500' :
                                  ministry.score >= 60 ? 'bg-amber-500' :
                                  'bg-red-500'
                                )}
                                style={{ width: `${ministry.score}%` }}
                              ></div>
                            </div>
                            <span className="font-bold text-slate-800">{ministry.score}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-bold text-slate-700">
                          {ministry.budgetExecution}%
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {ministry.projects}
                        </td>
                        <td className="px-4 py-3">
                          <Badge status={ministry.status} size="sm" />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Ministry Performance Chart */}
          <Card title="Ministry Performance Distribution">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ndp6Data.ministryPerformance.sort((a, b) => b.score - a.score)}>
                  <XAxis dataKey="ministry" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} height={100} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                    {ndp6Data.ministryPerformance.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.score >= 80 ? '#009543' :
                          entry.score >= 60 ? '#C4953B' :
                          '#D21034'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}

      {/* Trends & Gaps View */}
      {activeView === 'trends' && (
        <>
          {/* Critical Gaps */}
          <Card title="Critical Implementation Gaps">
            <div className="space-y-4">
              {ndp6Data.criticalGaps.map((gap, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'p-4 rounded-lg border',
                    gap.impact === 'High' ? 'bg-red-50 border-red-200' :
                    gap.impact === 'Medium' ? 'bg-amber-50 border-amber-200' :
                    'bg-blue-50 border-blue-200'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <SafeIcon
                      icon={FiIcons.FiAlertTriangle}
                      className={clsx(
                        'text-xl mt-0.5',
                        gap.impact === 'High' ? 'text-red-600' :
                        gap.impact === 'Medium' ? 'text-amber-600' :
                        'text-blue-600'
                      )}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded">
                          {gap.pillar}
                        </span>
                        <span className={clsx(
                          'px-2 py-1 rounded text-xs font-bold uppercase',
                          gap.impact === 'High' ? 'bg-red-100 text-red-700' :
                          gap.impact === 'Medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        )}>
                          {gap.impact} Impact
                        </span>
                      </div>
                      <h4 className="text-sm font-bold text-slate-900 mb-2">{gap.gap}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-slate-500 font-medium">Impact:</span>
                          <p className="text-slate-700 mt-1">{gap.impact}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 font-medium">Recommendation:</span>
                          <p className="text-slate-700 mt-1">{gap.recommendation}</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-2">
                          <SafeIcon icon={FiIcons.FiBuilding} className="text-slate-400" />
                          <span className="text-xs text-slate-600">
                            <strong>Responsible OMA:</strong> {gap.responsibleOMA}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Quarterly Trends Detailed */}
          <Card title="Quarterly Progress Trends (All Pillars)">
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ndp6Data.quarterlyTrends}>
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="EA" stroke="#003580" strokeWidth={3} name="Economic Advancement" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="ST" stroke="#009543" strokeWidth={3} name="Social Transformation" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="ES" stroke="#0085CA" strokeWidth={3} name="Environmental Sustainability" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="GG" stroke="#C4953B" strokeWidth={3} name="Good Governance" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Budget Execution by Pillar */}
          <Card title="Budget Execution by Pillar">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ndp6Data.pillars}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="budgetExecution" radius={[4, 4, 0, 0]}>
                    {ndp6Data.pillars.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.budgetExecution >= 80 ? '#009543' :
                          entry.budgetExecution >= 60 ? '#C4953B' :
                          '#D21034'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}

      {/* Legal Framework Notice */}
      <Card className="bg-grn-navy text-white">
        <div className="flex gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-xl shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-bold mb-2">NDP6 Legal & Policy Framework</p>
            <p className="opacity-90">
              The <strong>National Development Plan 6 (2022-2027)</strong> is Namibia's strategic development blueprint,
              adopted by Cabinet and tabled in Parliament. This monitoring platform implements the{' '}
              <strong>NDP6 Integrated Monitoring & Evaluation Plan (IMEP)</strong> framework, which mandates quarterly
              reporting from all OMAs. Data accuracy and timeliness are critical for evidence-based policymaking and
              accountability to the Namibian people.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};